// ============================================================
// FILE: PermissionManager.jsx — Dynamic roles from m_roles table
// ============================================================
import React, { useState, useEffect } from 'react';
import {
  Shield, Save, Check, X, Eye, Plus,
  Pencil, Trash2, Users, Loader2, AlertCircle,
  Database, RefreshCw
} from 'lucide-react';
import config from '../config';

const API = config.API_BASE_URL;

const PermissionManager = () => {
  const [roles, setRoles]                     = useState([]);
  const [allPerms, setAllPerms]               = useState([]);
  const [selectedRoleId, setSelectedRoleId]   = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [rolesLoading, setRolesLoading]       = useState(true);
  const [saving, setSaving]                   = useState(false);
  const [saveMsg, setSaveMsg]                 = useState('');
  const [users, setUsers]                     = useState([]);
  const [activeTab, setActiveTab]             = useState('permissions');
  const [fetchError, setFetchError]           = useState('');
  const [mRoles, setMRoles]                   = useState([]); // ← dynamic roles from m_roles table

  // { username: { current, draft, saving, msg } }
  const [userRoleState, setUserRoleState] = useState({});

  const getDisplayName = (u) =>
    u?.fullNames || u?.fullName ||
    (u?.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : null) ||
    u?.username || u?.userName || 'Unknown';

  const getInitial = (u) =>
    (getDisplayName(u)?.[0] || '?').toUpperCase();

  const getRoleBadgeClass = (role) => {
    const r = (role || '').toLowerCase();
    if (r.includes('super'))    return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
    if (r.includes('admin'))    return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
    if (r.includes('dev'))      return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
    if (r.includes('tech') || r.includes('it')) return 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30';
    if (r.includes('market'))   return 'bg-pink-500/20 text-pink-300 border border-pink-500/30';
    if (r.includes('staff'))    return 'bg-green-500/20 text-green-300 border border-green-500/30';
    return 'bg-slate-600/40 text-slate-300 border border-slate-600/40';
  };

  // ─── Load all data ────────────────────────────────────────
  const loadInitialData = () => {
    setRolesLoading(true);
    setFetchError('');

    Promise.all([
      // Permissions roles (for the permissions tab)
      fetch(`${API}/Permissions/roles`).then(r => {
        if (!r.ok) throw new Error(`Roles API error: ${r.status}`);
        return r.json();
      }),
      // All permissions
      fetch(`${API}/Permissions/all-permissions`).then(r => {
        if (!r.ok) throw new Error(`Permissions API error: ${r.status}`);
        return r.json();
      }),
      // All admins/users
      fetch(`${API}/Admin`).then(r => {
        if (!r.ok) throw new Error(`Admin API error: ${r.status}`);
        return r.json();
      }).catch(() => []),
      // Dynamic roles from m_roles table
      fetch(`${API}/Roles`).then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([rolesData, permsData, usersData, mRolesData]) => {
        setRoles(rolesData);
        setAllPerms(permsData);

        // Extract role names from m_roles
        const roleNames = Array.isArray(mRolesData)
          ? mRolesData
              .filter(r => r.isActive !== false && r.is_active !== false)
              .map(r => r.roleName ?? r.role_name ?? r.RoleName ?? r.name ?? '')
              .filter(Boolean)
          : [];
        setMRoles(roleNames);

        const list = Array.isArray(usersData) ? usersData : [];
        setUsers(list);

        const init = {};
        list.forEach(u => {
          const uname = u.username || u.userName;
          if (uname) {
            init[uname] = {
              current: u.role || '',
              draft:   u.role || '',
              saving:  false,
              msg:     '',
            };
          }
        });
        setUserRoleState(init);

        if (rolesData.length > 0) setSelectedRoleId(rolesData[0].id);
        else setFetchError('Walang roles sa database. I-run muna ang SQL script.');
      })
      .catch(err => setFetchError(err.message))
      .finally(() => setRolesLoading(false));
  };

  useEffect(() => { loadInitialData(); }, []);

  // ─── Fetch role permissions ───────────────────────────────
  useEffect(() => {
    if (!selectedRoleId || allPerms.length === 0) return;
    setLoading(true);

    fetch(`${API}/Permissions/roles/${selectedRoleId}`)
      .then(r => r.json())
      .then(data => {
        const permMap = {};
        (data.permissions || []).forEach(p => {
          permMap[p.permissionKey] = {
            canView:   p.canView,
            canCreate: p.canCreate,
            canEdit:   p.canEdit,
            canDelete: p.canDelete,
          };
        });
        const full = allPerms.map(p => ({
          permissionKey: p.permissionKey,
          label:         p.label,
          category:      p.category,
          canView:    permMap[p.permissionKey]?.canView   ?? false,
          canCreate:  permMap[p.permissionKey]?.canCreate ?? false,
          canEdit:    permMap[p.permissionKey]?.canEdit   ?? false,
          canDelete:  permMap[p.permissionKey]?.canDelete ?? false,
        }));
        setRolePermissions(full);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedRoleId, allPerms]);

  const togglePerm = (permKey, field) => {
    setRolePermissions(prev =>
      prev.map(p => p.permissionKey === permKey ? { ...p, [field]: !p[field] } : p)
    );
  };

  const toggleAll = (field, value) => {
    setRolePermissions(prev => prev.map(p => ({ ...p, [field]: value })));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(
        `${API}/Permissions/roles/${selectedRoleId}/permissions`,
        {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(rolePermissions),
        }
      );
      setSaveMsg(res.ok ? 'success' : 'error');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('error');
    } finally {
      setSaving(false);
    }
  };

  const setDraftRole = (username, value) => {
    setUserRoleState(prev => ({
      ...prev,
      [username]: { ...prev[username], draft: value }
    }));
  };

  // ─── Save role ────────────────────────────────────────────
  const handleSaveUserRole = async (username) => {
    const draft = userRoleState[username]?.draft;
    if (!draft || !username) return;

    setUserRoleState(prev => ({
      ...prev,
      [username]: { ...prev[username], saving: true, msg: '' }
    }));

    try {
      // Step 1: Get full existing admin data
      const getRes = await fetch(`${API}/Admin/${username}`);
      if (!getRes.ok) throw new Error('Failed to fetch admin data');
      const existing = await getRes.json();

      // Step 2: Build FormData with ALL existing fields + new role
      const formData = new FormData();
      formData.append('username',               existing.username               || '');
      formData.append('email',                  existing.email                  || '');
      formData.append('password',               existing.password               || '');
      formData.append('role',                   draft);
      formData.append('identificationCode',     existing.identificationCode     || '');
      formData.append('fullNames',              existing.fullNames              || '');
      formData.append('chatPermission',         existing.chatPermission         ?? false);
      formData.append('notificationPermission', existing.notificationPermission ?? false);
      formData.append('addUserPermission',      existing.addUserPermission      ?? false);
      formData.append('addAdminPermission',     existing.addAdminPermission     ?? false);
      formData.append('taskPermission',         existing.taskPermission         ?? false);
      formData.append('calendarPermission',     existing.calendarPermission     ?? false);
      formData.append('incidentPermission',     existing.incidentPermission     ?? false);
      formData.append('ticketlistPermission',   existing.ticketlistPermission   ?? false);
      formData.append('galleryPermission',      existing.galleryPermission      ?? false);
      formData.append('workloadPermission',     existing.workloadPermission     ?? false);
      formData.append('emailPermission',        existing.emailPermission        ?? false);
      formData.append('oncallPermission',       existing.oncallPermission       ?? false);
      formData.append('isApproved',             existing.isApproved             ?? false);
      formData.append('team',                   existing.team                   || '');
      formData.append('roleTeams',              existing.roleTeams              || '');

      // Step 3: PUT
      const putRes = await fetch(`${API}/Admin/update`, {
        method: 'PUT',
        body:   formData,
      });

      const msg = putRes.ok ? 'success' : 'error';
      setUserRoleState(prev => ({
        ...prev,
        [username]: {
          ...prev[username],
          saving:  false,
          msg,
          current: putRes.ok ? draft : prev[username].current,
        }
      }));

      if (putRes.ok) {
        setUsers(prev => prev.map(u =>
          (u.username || u.userName) === username ? { ...u, role: draft } : u
        ));
      }

      setTimeout(() => {
        setUserRoleState(prev => ({
          ...prev,
          [username]: { ...prev[username], msg: '' }
        }));
      }, 3000);

    } catch (err) {
      console.error(err);
      setUserRoleState(prev => ({
        ...prev,
        [username]: { ...prev[username], saving: false, msg: 'error' }
      }));
    }
  };

  // Fallback roles kung hindi pa nag-load yung m_roles
  const dropdownRoles = mRoles.length > 0
    ? mRoles
    : ['Superadmin', 'Admin', 'Staff', 'Developer', 'Marketing', 'Technical', 'IT', 'Tech'];

  const menuPerms = rolePermissions.filter(p => p.category === 'menu');

  // ─── Error state ──────────────────────────────────────────
  if (!rolesLoading && (fetchError || roles.length === 0)) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-10 max-w-lg w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Database size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Walang Data</h2>
          <p className="text-slate-400 text-sm mb-2">Hindi pa na-run ang SQL permissions script.</p>
          {fetchError && (
            <p className="text-red-400 text-xs font-mono bg-slate-900 rounded-lg px-3 py-2 mb-4">
              {fetchError}
            </p>
          )}
          <div className="bg-slate-900 rounded-xl p-4 text-left mb-6">
            <p className="text-xs text-slate-500 uppercase font-bold mb-2 tracking-wide">Dapat gawin:</p>
            <ol className="text-sm text-slate-300 space-y-1 list-decimal list-inside">
              <li>Buksan ang SSMS</li>
              <li>I-run ang <span className="text-blue-400 font-mono">01_SQL_Permissions.sql</span></li>
              <li>I-restart ang backend (IIS)</li>
              <li>I-refresh ang page</li>
            </ol>
          </div>
          <button onClick={loadInitialData}
            className="flex items-center gap-2 mx-auto bg-blue-600 hover:bg-blue-700
              text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition">
            <RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600/50
          rounded-2xl p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <Shield className="text-blue-400" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Permission Manager</h1>
                <p className="text-slate-400 text-sm mt-0.5">Control what each role can see and do</p>
              </div>
            </div>
            <button onClick={loadInitialData}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600
                border border-slate-600 text-slate-300 px-4 py-2 rounded-xl text-sm transition">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'permissions', label: 'Role Permissions',      icon: Shield },
            { key: 'users',       label: 'Assign Roles to Users', icon: Users },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold
                  text-sm transition-all ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                }`}>
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── PERMISSIONS TAB ── */}
        {activeTab === 'permissions' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-slate-400">Select Role:</span>
                <div className="flex gap-2 flex-wrap">
                  {rolesLoading ? (
                    <div className="flex gap-2">
                      {[1,2,3].map(i => (
                        <div key={i} className="w-24 h-9 bg-slate-700 rounded-xl animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    roles.map(role => (
                      <button key={role.id} onClick={() => setSelectedRoleId(role.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                          selectedRoleId === role.id
                            ? role.roleName === 'Super Admin'
                              ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                              : role.roleName === 'Admin'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-slate-500 text-white'
                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600 border border-slate-600'
                        }`}>
                        {role.roleName}
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {saveMsg === 'success' && (
                  <span className="text-green-400 text-sm flex items-center gap-1 font-medium">
                    <Check size={15} /> Saved!
                  </span>
                )}
                {saveMsg === 'error' && (
                  <span className="text-red-400 text-sm flex items-center gap-1 font-medium">
                    <AlertCircle size={15} /> Failed
                  </span>
                )}
                <button onClick={handleSave} disabled={saving || !selectedRoleId}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-500
                    text-white px-5 py-2.5 rounded-xl font-semibold text-sm
                    transition-all shadow-md disabled:opacity-50">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Quick Toggle Bar */}
            <div className="px-5 py-3 bg-slate-900/50 border-b border-slate-700 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Quick:</span>
              {[
                { label: '✓ All View',      field: 'canView',   val: true,  cls: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30' },
                { label: '✗ Remove View',   field: 'canView',   val: false, cls: 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30' },
                { label: '✓ All Create',    field: 'canCreate', val: true,  cls: 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30' },
                { label: '✗ Remove Delete', field: 'canDelete', val: false, cls: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30' },
              ].map(btn => (
                <button key={btn.label} onClick={() => toggleAll(btn.field, btn.val)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${btn.cls}`}>
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Permissions Table */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="animate-spin text-blue-500" size={36} />
                <span className="text-slate-500 text-sm">Loading permissions...</span>
              </div>
            ) : menuPerms.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Database size={36} className="text-slate-600" />
                <span className="text-slate-500 text-sm">Walang permissions data.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-900/80 border-b border-slate-700">
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Feature / Page
                      </th>
                      {[
                        { label: 'View',   icon: Eye },
                        { label: 'Create', icon: Plus },
                        { label: 'Edit',   icon: Pencil },
                        { label: 'Delete', icon: Trash2 },
                      ].map(col => {
                        const Icon = col.icon;
                        return (
                          <th key={col.label} className="px-4 py-4 text-center text-xs font-bold
                            text-slate-400 uppercase tracking-wider">
                            <div className="flex items-center justify-center gap-1">
                              <Icon size={13} /> {col.label}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {menuPerms.map((perm, idx) => (
                      <tr key={perm.permissionKey}
                        className={`transition-colors group ${
                          idx % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'
                        } hover:bg-slate-700/50`}>
                        <td className="px-6 py-3">
                          <span className="text-sm font-semibold text-slate-200">{perm.label}</span>
                          <span className="ml-2 text-xs text-slate-600 font-mono group-hover:text-slate-500 transition">
                            {perm.permissionKey}
                          </span>
                        </td>
                        {['canView', 'canCreate', 'canEdit', 'canDelete'].map(field => (
                          <td key={field} className="px-4 py-3 text-center">
                            <button onClick={() => togglePerm(perm.permissionKey, field)}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center
                                mx-auto transition-all ${
                                perm[field]
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/40'
                                  : 'bg-slate-700 text-slate-600 hover:bg-slate-600 border border-slate-600'
                              }`}>
                              {perm[field] ? <Check size={15} /> : <X size={15} />}
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── USERS TAB ── */}
        {activeTab === 'users' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-bold text-white">Assign Roles to Users</h2>
                <p className="text-sm text-slate-500 mt-0.5">Change the role of each user then hit Save</p>
              </div>
              {/* Role count badge */}
              {mRoles.length > 0 && (
                <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30
                  px-3 py-1.5 rounded-full font-semibold">
                  {mRoles.length} roles available
                </span>
              )}
            </div>

            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Users size={36} className="text-slate-600" />
                <span className="text-slate-500 text-sm">Walang users.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-900/80 border-b border-slate-700">
                      {['User', 'Email', 'Current Role', 'Change Role', ''].map((h, i) => (
                        <th key={i} className="px-6 py-4 text-left text-xs font-bold
                          text-slate-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {users.map((user, idx) => {
                      const username = user?.username || user?.userName;
                      const state    = userRoleState[username] || {};

                      return (
                        <tr key={`user-${username}-${idx}`}
                          className={`transition-colors hover:bg-slate-700/40 ${
                            idx % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'
                          }`}>

                          {/* User */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br
                                from-blue-500 to-indigo-600 flex items-center justify-center
                                text-white font-bold text-sm flex-shrink-0">
                                {getInitial(user)}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-slate-200">
                                  {getDisplayName(user)}
                                </div>
                                <div className="text-xs text-slate-500">@{username}</div>
                              </div>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="px-6 py-4 text-sm text-slate-400">
                            {user.email || '—'}
                          </td>

                          {/* Current Role badge */}
                          <td className="px-6 py-4">
                            {state.current ? (
                              <span className={`text-xs px-3 py-1 rounded-full font-semibold
                                ${getRoleBadgeClass(state.current)}`}>
                                {state.current}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-600 italic">No role</span>
                            )}
                          </td>

                          {/* Dropdown — dynamic from m_roles */}
                          <td className="px-6 py-4">
                            <select
                              value={state.draft || ''}
                              onChange={e => setDraftRole(username, e.target.value)}
                              className="text-sm bg-slate-700 border border-slate-600 text-slate-200
                                rounded-lg px-3 py-2 focus:outline-none focus:ring-2
                                focus:ring-blue-500 cursor-pointer hover:bg-slate-600 transition
                                min-w-[160px]"
                            >
                              <option value="">-- Select Role --</option>
                              {dropdownRoles.map(r => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          </td>

                          {/* Save button */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              {state.msg === 'success' && (
                                <span className="text-green-400 text-xs flex items-center gap-1 font-medium">
                                  <Check size={13} /> Saved!
                                </span>
                              )}
                              {state.msg === 'error' && (
                                <span className="text-red-400 text-xs flex items-center gap-1 font-medium">
                                  <AlertCircle size={13} /> Failed
                                </span>
                              )}
                              <button
                                onClick={() => handleSaveUserRole(username)}
                                disabled={state.saving || !state.draft || state.draft === state.current}
                                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500
                                  text-white px-4 py-2 rounded-xl font-semibold text-xs
                                  transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {state.saving
                                  ? <Loader2 size={13} className="animate-spin" />
                                  : <Save size={13} />}
                                {state.saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default PermissionManager;